const clamp = ( value: number, min: number, max: number ): number =>
	Math.min( Math.max( value, min ), max );

const hexToRgb = ( hex: string ): { r: number; g: number; b: number } => {
	const normalizedHex = hex.replace( '#', '' );
	const expandedHex =
		normalizedHex.length === 3
			? normalizedHex
				.split( '' )
				.map( ( character ) => `${ character }${ character }` )
				.join( '' )
			: normalizedHex;
	const colorInt = parseInt( expandedHex, 16 );

	return {
		r: ( colorInt >> 16 ) & 255,
		g: ( colorInt >> 8 ) & 255,
		b: colorInt & 255,
	};
};

const rgbToHex = ( r: number, g: number, b: number ): string =>
	`#${ [
		r,
		g,
		b
	]
		.map( ( channel ) => Math.round( channel ).toString( 16 ).padStart( 2, '0' ) )
		.join( '' ) }`;

const rgbToHsl = (
	r: number,
	g: number,
	b: number
): { h: number; s: number; l: number } => {
	const normalizedRed = r / 255;
	const normalizedGreen = g / 255;
	const normalizedBlue = b / 255;
	const maxChannel = Math.max( normalizedRed, normalizedGreen, normalizedBlue );
	const minChannel = Math.min( normalizedRed, normalizedGreen, normalizedBlue );
	const delta = maxChannel - minChannel;
	const lightness = ( maxChannel + minChannel ) / 2;
	let hue = 0;
	let saturation = 0;

	if ( delta !== 0 ) {
		saturation = delta / ( 1 - Math.abs( 2 * lightness - 1 ) );

		switch ( maxChannel ) {
			case normalizedRed:
				hue = 60 * ( ( normalizedGreen - normalizedBlue ) / delta );
				break;
			case normalizedGreen:
				hue = 60 * ( ( normalizedBlue - normalizedRed ) / delta + 2 );
				break;
			default:
				hue = 60 * ( ( normalizedRed - normalizedGreen ) / delta + 4 );
		}
	}

	if ( hue < 0 ) {
		hue += 360;
	}

	return { h: hue, s: saturation, l: lightness };
};

const hslToRgb = (
	h: number,
	s: number,
	l: number
): { r: number; g: number; b: number } => {
	const chroma = ( 1 - Math.abs( 2 * l - 1 ) ) * s;
	const huePrime = h / 60;
	const secondComponent = chroma * ( 1 - Math.abs( ( huePrime % 2 ) - 1 ) );
	const matchLightness = l - chroma / 2;
	let redPrime = 0;
	let greenPrime = 0;
	let bluePrime = 0;

	if ( huePrime >= 0 && huePrime < 1 ) {
		redPrime = chroma;
		greenPrime = secondComponent;
	} else if ( huePrime < 2 ) {
		redPrime = secondComponent;
		greenPrime = chroma;
	} else if ( huePrime < 3 ) {
		greenPrime = chroma;
		bluePrime = secondComponent;
	} else if ( huePrime < 4 ) {
		greenPrime = secondComponent;
		bluePrime = chroma;
	} else if ( huePrime < 5 ) {
		redPrime = secondComponent;
		bluePrime = chroma;
	} else {
		redPrime = chroma;
		bluePrime = secondComponent;
	}

	return {
		r: ( redPrime + matchLightness ) * 255,
		g: ( greenPrime + matchLightness ) * 255,
		b: ( bluePrime + matchLightness ) * 255,
	};
};

export const adjustLightness = ( hex: string, amount: number ): string => {
	const { r, g, b } = hexToRgb( hex );
	const { h, s, l } = rgbToHsl( r, g, b );
	const adjustedLightness = clamp( l + amount / 100, 0, 1 );
	const adjustedRgb = hslToRgb( h, s, adjustedLightness );

	return rgbToHex( adjustedRgb.r, adjustedRgb.g, adjustedRgb.b );
};
